import React from 'react';
import PropTypes from 'prop-types';

import {
  // Types
  Color,
  FontSize,
  FontWeight,
  // Lists
  colorList,
  fontWeightList,
  // ComponentSizes
  COMPONENT_SIZE_MEDIUM,
  COMPONENT_SIZE_SMALL,
  COMPONENT_SIZE_DYNAMIC,
  ComponentSize,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames, IClassNames } from '../../../utils/styles';
import Text from '../text/text';

import './player-formation-place-icon.scss';

export const componentSizeListCustom: ComponentSize[] = [
  COMPONENT_SIZE_SMALL,
  COMPONENT_SIZE_MEDIUM,
  COMPONENT_SIZE_DYNAMIC,
];

export interface IPlayerFormationPlaceIconLabel extends IComponent {
  fontSizeLabel?: FontSize;
  fontWeightLabel?: FontWeight;
  colorLabel?: Color;
}

export interface IPlayerFormationPlaceIconName extends IComponent {
  fontSizeName?: FontSize;
  fontWeightName?: FontWeight;
  colorName?: Color;
}
export interface IPlayerFormationPlaceIcon extends IPlayerFormationPlaceIconLabel, IPlayerFormationPlaceIconName {
  readonly isCircled?: boolean;
  readonly name: string;
  readonly label: string;
  readonly altColor?: boolean;
}

const classNames = (size: ComponentSize, isCircled: boolean, altColor: boolean): IClassNames =>
  makeClassNames({
    PlayerFormationPlaceIcon: ['player-formation-place-icon', size && `player-formation-place-icon--${size}`],
    PlayerFormationPlaceIconShape: [
      'player-formation-place-icon-shape',
      isCircled && `player-formation-place-icon-shape--circled`,
      !altColor && `player-formation-place-icon-shape--alternative-color`,
    ],
    PlayerFormationPlaceIconNumber: 'player-formation-place-icon-number',
    PlayerFormationPlaceIconName: 'player-formation-place-icon-name',
  });

const PlayerFormationPlaceIcon: React.FC<IPlayerFormationPlaceIcon> = ({
  /* istanbul ignore next: default value on mandatory field */ isCircled = true,
  name,
  /* istanbul ignore next: default value on mandatory field */ colorName = 'light',
  /* istanbul ignore next: default value on mandatory field */ fontWeightName = '500',
  label,
  /* istanbul ignore next: default value on mandatory field */ colorLabel = 'blue',
  /* istanbul ignore next: default value on mandatory field */ fontWeightLabel = '500',
  /* istanbul ignore next: default value on mandatory field */ size = COMPONENT_SIZE_SMALL,
  /* istanbul ignore next: default value on mandatory field */ altColor = false,
}) => {
  const cn = classNames(size, isCircled, altColor);
  const fontsize = size === 'small' ? '12' : '12--14';
  return (
    <div className={cn.PlayerFormationPlaceIcon}>
      <div className={cn.PlayerFormationPlaceIconShape}>
        {label && (
          <Text
            className={cn.PlayerFormationPlaceIconNumber}
            tag="span"
            fontSize={fontsize}
            fontWeight={fontWeightLabel}
            color={altColor ? colorLabel : 'light'}
          >
            {label}
          </Text>
        )}
      </div>
      {name && (
        <Text
          className={cn.PlayerFormationPlaceIconName}
          tag="p"
          fontSize={fontsize}
          fontWeight={fontWeightName}
          color={colorName}
        >
          {name}
        </Text>
      )}
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  PlayerFormationPlaceIcon.propTypes = {
    altColor: PropTypes.bool,
    colorLabel: PropTypes.oneOf(colorList),
    colorName: PropTypes.oneOf(colorList),
    fontWeightLabel: PropTypes.oneOf(fontWeightList),
    fontWeightName: PropTypes.oneOf(fontWeightList),
    isCircled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    size: PropTypes.oneOf(componentSizeListCustom),
  };
}

export default PlayerFormationPlaceIcon;
